import React from 'react';
import { Collapse, Table, Typography } from 'antd';
import { useGetPurchaseAssessmentDetailQuery } from '@/pages/mortgage/apis/purchaseAPI';
import { AnyObject } from 'yup';
import { getCountryData } from 'countries-list';
import { useGetRefinanceAssessmentDetailQuery } from '@/pages/mortgage/apis/refinanceAPI';

const { Title } = Typography;

const { Panel } = Collapse;

interface StepData {
  step: string;
  data: Record<string, any>;
}

type ReviewDataProps = {
  stepData: StepData[];
  id: string;
  type?: 'purchase' | 'refinance';
};

const formatLabel = (label: string) =>
  label
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const mapCamelCaseToTitleCase = (label: string) => {
  if (/^[A-Z0-9\s]+$/.test(label)) {
    return label;
  }
  return label
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatValue = (value: any) => {
  if (Array.isArray(value))
    return value.length ? mapCamelCaseToTitleCase(value.join(', ')) : 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === '' || value === null || value === undefined) return 'N/A';
  if (typeof value === 'string') return mapCamelCaseToTitleCase(value);
  return value.toString();
};

const ReviewData = ({ stepData, id, type = 'purchase' }: ReviewDataProps) => {
  console.log('stepData', stepData);
  const purchaseQuery = useGetPurchaseAssessmentDetailQuery(
    { application_id: id },
    {
      skip: type !== 'purchase',
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    },
  );

  const refinanceQuery = useGetRefinanceAssessmentDetailQuery(
    { application_id: id },
    {
      skip: type !== 'refinance',
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    },
  );

  const assessmentDetail =
    type === 'purchase' ? purchaseQuery.data : refinanceQuery.data;

  const properties = assessmentDetail?.data?.application?.properties || [];

  const formatPropertiesData = (propertyDetails: Record<string, any>) =>
    Object.entries(propertyDetails).map(([key, value]) => {
      if (value === null || value === undefined || value === '') return null;
      if (key === 'country') {
        return {
          key,
          label: formatLabel(key),
          value: getCountryData(value)?.name,
        };
      }
      return {
        key,
        label: formatLabel(key),
        value: formatValue(value),
      };
    });
  const columns = [
    {
      title: 'Field',
      dataIndex: 'label',
      key: 'label',
      width: '40%',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
    },
  ];

  const australianProperties = properties.filter(
    (property: any) =>
      property.property_location?.toLowerCase() === 'australian',
  );

  const overseasProperties = properties.filter(
    (property: any) => property.property_location?.toLowerCase() === 'overseas',
  );
  return (
    <div style={{ paddingBottom: '1rem 0rem' }}>
      {stepData.map((step, index) => {
        const dataSource = Object.entries(step.data || {})
          .map(([key, value]) => {
            if (value === null || value === undefined || value === '')
              return null;
            if (key === 'country') {
              return {
                key,
                label: formatLabel(key),
                value: getCountryData(value)?.name,
              };
            }
            return {
              key,
              label: formatLabel(key),
              value: formatValue(value),
            };
          })
          .filter(Boolean);

        return (
          <div key={index}>
            <Title
              level={5}
              style={{
                textTransform: 'capitalize',
                borderBottom: '1px solid #f0f0f0',
                margin: '0',
                padding: '1rem 0rem',
              }}
            >
              {step.step.replace(/_/g, ' ')}
            </Title>
            <Table
              columns={columns}
              dataSource={dataSource as AnyObject[]}
              pagination={false}
              bordered
              size="middle"
            />
          </div>
        );
      })}
      {(australianProperties.length > 0 || overseasProperties.length > 0) && (
        <div>
          {australianProperties.length > 0 && (
            <>
              <Title level={5} style={{ marginTop: '1rem' }}>
                Australian Properties
              </Title>
              <Collapse
                accordion
                bordered={false}
                style={{ border: 'none', backgroundColor: 'transparent' }}
              >
                {australianProperties.map((property: any, idx: number) => (
                  <Panel
                    header={
                      property.property_details?.property_address +
                      '  (' +
                      property?.property_location +
                      ' property)'
                    }
                    key={`australian-${idx}`}
                    style={{
                      backgroundColor: '#fbfbfb',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                    }}
                  >
                    <Table
                      columns={columns}
                      dataSource={
                        formatPropertiesData(
                          property.property_details || {},
                        ) as AnyObject[]
                      }
                      pagination={false}
                      bordered
                      size="middle"
                    />
                  </Panel>
                ))}
              </Collapse>
            </>
          )}

          {overseasProperties.length > 0 && (
            <>
              <Title level={5} style={{ marginTop: '1rem' }}>
                Overseas Properties
              </Title>
              <Collapse
                accordion
                bordered={false}
                style={{ border: 'none', backgroundColor: 'transparent' }}
              >
                {overseasProperties.map((property: any, idx: number) => (
                  <Panel
                    header={
                      property.property_details?.property_address +
                      '  (' +
                      property?.property_location +
                      ' property)'
                    }
                    key={`overseas-${idx}`}
                    style={{
                      backgroundColor: '#fbfbfb',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                    }}
                  >
                    <Table
                      columns={columns}
                      dataSource={
                        formatPropertiesData(
                          property.property_details || {},
                        ) as AnyObject[]
                      }
                      pagination={false}
                      bordered
                      size="middle"
                    />
                  </Panel>
                ))}
              </Collapse>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewData;
